import { FC, useCallback, useEffect, useRef } from "react";
import { useThemeStore } from "../../stores";
import { Column, Row } from "../containers";
import { JustifyContent } from "../containers/types";
import { Heading } from "../typography";
import { RowEvent, TableProps, TableRendererProps } from "./types";
import { useResize } from "../../hooks";

const pxToNumber = (px: string): number => {
  return Number(px.substr(0, px.length - 2));
};

const Table = <T extends {}>({
  data,
  label,
  minWidth,
  renderer,
  layout,
  rowEvents,
}: TableProps<T>) => {
  const theme = useThemeStore((s) => s.theme);

  const headerRefs = useRef<(HTMLHeadingElement | null)[]>([]);
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  const calculateItemsWidth = useCallback(() => {
    if (!headerRefs.current.length) return;

    refs.current.forEach((ref) => {
      if (!ref) return;

      Array.from(ref.children).forEach((c, idx) => {
        let el = c as HTMLElement;

        const horizontalPadding =
          pxToNumber(window.getComputedStyle(el).paddingLeft) +
          pxToNumber(window.getComputedStyle(el).paddingRight);

        const headerWidth = window.getComputedStyle(
          headerRefs.current[idx] as HTMLHeadingElement
        ).width;

        el.style.minWidth = `calc(${headerWidth} - ${horizontalPadding}px)`;
        el.style.textAlign = "center";
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs, headerRefs, data, layout, renderer]);

  useResize(calculateItemsWidth);

  useEffect(() => {
    setTimeout(calculateItemsWidth, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback(
    function (
      this: { re: RowEvent<T>; idx: number },
      e: MouseEvent | TouchEvent
    ): void {
      this.re.handler(e as MouseEvent & TouchEvent, data[this.idx]);
    },
    [data]
  );

  useEffect(() => {
    if (!rowEvents) return;
    rowEvents.forEach((re) => {
      refs.current.forEach((ref, idx) => {
        if (!ref) return;
        ref.addEventListener(re.type, t.bind({ re, idx }));
      });
    });
    const cpy = refs.current;
    return (): void => {
      rowEvents.forEach((re) => {
        cpy.forEach((ref, idx) => {
          if (!ref) return;
          ref.removeEventListener(re.type, t.bind({ re, idx }));
        });
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, rowEvents?.length]);

  let sumLayouts = 0;
  if (layout) {
    Object.keys(layout).forEach((k) => (sumLayouts += layout[k as keyof T]));
  }
  const calculatedMinWidth = minWidth ? minWidth : layout ? sumLayouts : "auto";

  return (
    <Column
      style={{
        width: "100%",
        minHeight: 200,
        minWidth: calculatedMinWidth,
      }}
    >
      <Row
        style={{ width: "100%", marginBottom: "1rem" }}
        justifyContent={JustifyContent.Evenly}
      >
        {Object.keys(label).map((k, idx) => (
          <Heading
            key={k}
            textStyles={{
              minWidth: !layout ? "100%" : layout[k as keyof T],
              width: "100%",
              textAlign: "center",
            }}
            color={theme.textOnBg}
            size={14}
            ref={(ref) => {
              if (!idx) headerRefs.current = [];
              headerRefs.current.push(ref);
            }}
          >
            {label[k as keyof T]}
          </Heading>
        ))}
      </Row>
      <Column
        style={{ width: "100%", borderRadius: 15, padding: "20px 0" }}
        background={theme.background}
      >
        {data.map((d, idx) => (
          <div
            key={idx}
            style={{
              width: "100%",
              padding: "0.5rem 0",
              borderBottom: `1px solid ${theme.overlay}`,
            }}
            className="row justify-content-evenly align-items-center transition"
            ref={(ref) => {
              if (!idx) refs.current = [];
              refs.current.push(ref);
            }}
          >
            {Object.keys(label).map((k) => {
              const Renderer = renderer[k as keyof T] as FC<TableRendererProps>;
              return <Renderer props={d[k as keyof T]} key={k} />;
            })}
          </div>
        ))}
      </Column>
    </Column>
  );
};

export default Table;
